import { atom } from "jotai";
import { canvasIdAtom } from "../canvas";
import { nodesAtom } from "./nodesAtom";
import { updateNodeSetter } from "./updateNodeSetter";
import { saveCanvasSetter } from "../persistence";
import { createNotificationSetter } from "../notifications";
import { emitUserInteractionEventSetter } from "../events";

interface ImageUploadResponse {
  id: string;
}

type UploadImageParams = {
  nodeId: string;
  file: File;
  uploadMethod?: "drag_and_drop" | "copy_and_paste";
  blobUrl: string;
};

/**
 * Verifies that an image URL is valid and accessible
 * Returns a promise that resolves to true if the image loads successfully
 * Times out after 5 seconds
 */
async function verifyImageUrl(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => resolve(false), 5000);
    img.onload = () => {
      clearTimeout(timeout);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };
    img.src = url;
  });
}

/**
 * Uploads image to server and updates node with server URL
 * Removes node if upload fails
 *
 * Permission flow:
 * - Backend requires CAN_EDIT permission for upload (checked server-side)
 * - After upload, images are accessible based on canvas permissions
 * - Public canvases allow viewing without auth; upload still requires CAN_EDIT
 */
export const uploadImageToServerSetter = atom(
  null,
  async (
    get,
    set,
    { nodeId, file, uploadMethod, blobUrl }: UploadImageParams,
  ) => {
    const canvasId = get(canvasIdAtom);
    if (!canvasId) {
      console.error("Cannot upload image: canvas ID not available");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        `/api/canvas/v1/canvases/${canvasId}/images`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        set(createNotificationSetter, {
          type: "error",
          message:
            "Something went wrong while uploading. Please try again, or upload a different file if the issue continues.",
        });
        return;
      }

      const { id } = (await response.json()) as ImageUploadResponse;
      const imageUrl = `/api/canvas/v1/canvases/${canvasId}/images/${id}`;

      const isImageValid = await verifyImageUrl(imageUrl);
      if (!isImageValid) {
        throw new Error("Uploaded image is not accessible");
      }

      // Update node with server URL
      // ImageNode component will handle any loading errors gracefully
      const updatedNodes = set(updateNodeSetter, {
        id: nodeId,
        data: {
          imgUrl: imageUrl,
        },
      });
      set(saveCanvasSetter, { nodes: updatedNodes });

      // Revoke blob URL now that we have the server URL
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }

      // Track successful upload
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
      const fileType = file.type || "unknown";
      const method = uploadMethod || "unknown";
      const eventAction =
        uploadMethod === "drag_and_drop" ? "drag_and_drop" : "click";

      await set(emitUserInteractionEventSetter, {
        eventAction,
        interactionGoal: "Image upload - success",
        rawText: `Image uploaded: ${fileSizeMB} MB, ${fileType}, ${method}`,
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      if (
        error instanceof Error &&
        error.message.includes("Unsupported image type")
      ) {
        set(createNotificationSetter, {
          type: "error",
          message:
            "This file type isn't supported. Please upload a supported image format like PNG, JPG, SVG, WebP, AVIF, or GIF.",
        });
      }

      // Remove the node if upload fails
      const nodes = get(nodesAtom);
      const updatedNodes = nodes.filter((node) => node.id !== nodeId);
      set(nodesAtom, updatedNodes);
      set(saveCanvasSetter, { nodes: updatedNodes });

      // Revoke blob URL on error to prevent memory leak
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  },
);

uploadImageToServerSetter.debugLabel = "uploadImageToServerSetter";
